// Phase 5c-3 — cross-portfolio proof/evidence status board. Reads only
// viewer-projected RPCs: ListPrograms drops programs the principal cannot see
// (and masks fields per the §14 ladder), and each per-program read is gated
// by ryugu's projection interceptor.
//
// Contract model v2 (D113): the deliverable rows are one ListOccurrences per
// program over its current contract period (D104's KPI basis) — each date with
// its computed status and its latest filing's attachments — grouped by
// deliverable. The tiles are the same reads' state_counts and on_time, summed
// across programs (Σ onTime / Σ timingKnown). A COR waiver is a date's own
// "Waived by the COR" status now, so the board no longer reads deviations.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraDataTable, SpecraEmptyState, SpecraFilterToolbar, SpecraInlineBanner, SpecraLink, SpecraMetricStrip, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { ErrorState, PageBody, PageHero } from '../../../components/layout';
import { deliveryClient } from '../../../lib/connect/clients';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { fetchAllPages } from '../_shared/lib/fetchAllPages';
import { countsOnTimePercent, sumCounts, useDeliverableCounts, useDutiesFanOut } from '../CommandCenter/hooks';
import { CmStatusBadge } from '../Deliverables/components/common';
import { OccurrenceWindow, useOccurrencesFanOut } from '../Deliverables/hooks';
import { cmDate, todayISO } from '../Deliverables/lib/format';
import { isOpenState, OCCURRENCE_STATES, occurrenceStatus, STATE_SHORT_LABEL } from '../Deliverables/lib/status';
// ryugu clamps ListPrograms pages to 100 (and silently defaults to 25 when
// no pagination is sent), so the board drains every page — a "portfolio
// wide" view must not quietly stop at the first one. 50 pages = 5000
// programs is a runaway guard, not an expected ceiling.
const PROGRAMS_PAGE_SIZE = 100;
const PROGRAMS_MAX_PAGES = 50;
const PROGRAMS_CAP = PROGRAMS_PAGE_SIZE * PROGRAMS_MAX_PAGES;
/** Every date in each program's current contract period (page_size 0 = all rows). */ const BOARD_QUERY = {
    window: OccurrenceWindow.CONTRACT_CURRENT_PERIOD
};
function useAllPrograms(companyId) {
    return useQuery({
        queryKey: [
            'evidence-board-programs',
            companyId
        ],
        queryFn: ()=>fetchAllPages(async (page)=>{
                const resp = await deliveryClient.listPrograms({
                    companyId,
                    pagination: {
                        page,
                        pageSize: PROGRAMS_PAGE_SIZE
                    }
                });
                return resp.programs;
            }, PROGRAMS_PAGE_SIZE, PROGRAMS_MAX_PAGES),
        staleTime: 30000
    });
}
const PROOF_FILTER_LABEL = {
    all: 'All filings',
    attached: 'Filed with attachments',
    no_attachments: 'Filed without attachments',
    not_filed: 'Not filed'
};
function proofOf(o) {
    if (!o.latestSubmission) return 'not_filed';
    return o.latestSubmission.attachments.length > 0 ? 'attached' : 'no_attachments';
}
function groupRows(rows, duties) {
    const groups = new Map();
    for (const r of rows){
        const key = `${r.programId}:${r.occurrence.dutyId}`;
        let g = groups.get(key);
        if (!g) {
            g = {
                key,
                programId: r.programId,
                programName: r.programName,
                dutyId: r.occurrence.dutyId,
                duty: duties.get(r.occurrence.dutyId),
                rows: []
            };
            groups.set(key, g);
        }
        g.rows.push(r.occurrence);
    }
    for (const g of groups.values())g.rows.sort((a, b)=>a.dueOn.localeCompare(b.dueOn));
    return [
        ...groups.values()
    ].sort((a, b)=>a.programName.localeCompare(b.programName) || (a.duty?.title ?? '').localeCompare(b.duty?.title ?? ''));
}
export function EvidenceBoardPage() {
    const tenantId = useTenantId();
    const { data: programsData, isLoading: programsLoading, error: programsError, refetch: refetchPrograms } = useAllPrograms(tenantId);
    const programs = useMemo(()=>programsData?.items ?? [], [
        programsData
    ]);
    const programsTruncated = programsData?.truncated ?? false;
    const programIds = useMemo(()=>programs.map((p)=>p.id), [
        programs
    ]);
    // Same request as the counts below, so both share one query per program.
    const occurrenceQueries = useOccurrencesFanOut(programIds, BOARD_QUERY);
    const counts = useDeliverableCounts(programIds, BOARD_QUERY);
    const duties = useDutiesFanOut(programIds);
    const [programFilter, setProgramFilter] = useState('all');
    const [stateFilter, setStateFilter] = useState('all');
    const [proofFilter, setProofFilter] = useState('all');
    const [expandedKey, setExpandedKey] = useState(null);
    const toggleGroup = (key)=>setExpandedKey((cur)=>cur === key ? null : key);
    const programName = (p)=>p.programNameShort || p.programNameFull || p.contractNumber || 'Unnamed program';
    const rows = programs.flatMap((p, i)=>(occurrenceQueries[i]?.data?.occurrences ?? []).map((occurrence)=>({
                programId: p.id,
                programName: programName(p),
                occurrence
            })));
    const visible = rows.filter((r)=>(programFilter === 'all' || r.programId === programFilter) && (stateFilter === 'all' || r.occurrence.status?.state === stateFilter) && (proofFilter === 'all' || proofOf(r.occurrence) === proofFilter));
    const groups = groupRows(visible, duties);
    // Tiles: the program filter narrows them to that program's counts; the
    // state and proof filters narrow rows only (the tiles are the denominators).
    const tileCounts = programFilter === 'all' ? counts.total : counts.byProgram.get(programFilter) ?? sumCounts([]);
    const tileRows = rows.filter((r)=>programFilter === 'all' || r.programId === programFilter);
    const filed = tileRows.filter((r)=>r.occurrence.latestSubmission);
    const filedWithProof = filed.filter((r)=>proofOf(r.occurrence) === 'attached');
    const fanoutLoading = programsLoading || occurrenceQueries.some((q)=>q.isLoading);
    const failedPrograms = counts.failed;
    if (programsError) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx(ErrorState, {
                title: "Couldn't load the evidence board",
                message: "We couldn't load your programs. This is usually temporary — try again.",
                onRetry: ()=>void refetchPrograms()
            })
        });
    }
    const onTimePct = countsOnTimePercent(tileCounts);
    const clearFilters = ()=>{
        setProgramFilter('all');
        setStateFilter('all');
        setProofFilter('all');
    };
    const metrics = [
        {
            id: Testids.cmPortfolio.tileOnTime,
            label: 'On time',
            value: onTimePct === null ? 'No approvals yet' : `${onTimePct}%`,
            muted: onTimePct === null,
            note: onTimePct === null ? undefined : `${tileCounts.onTime} of ${tileCounts.timingKnown} approved with a known date`
        },
        {
            id: Testids.cmPortfolio.tileLate,
            label: STATE_SHORT_LABEL.late,
            value: tileCounts.stateCounts.late ?? 0,
            note: `of ${tileCounts.total} dates`
        },
        {
            id: Testids.cmPortfolio.tileAwaitingReview,
            label: STATE_SHORT_LABEL.awaiting_review,
            value: tileCounts.stateCounts.awaiting_review ?? 0,
            note: `of ${tileCounts.total} dates`
        },
        {
            id: Testids.evidenceBoard.summaryProofAttached,
            label: 'Proof on file',
            value: filed.length === 0 ? 'Nothing filed yet' : filedWithProof.length,
            muted: filed.length === 0,
            note: filed.length === 0 ? undefined : `of ${filed.length} filed ${filed.length === 1 ? 'date' : 'dates'} carry attachments`
        }
    ];
    return /*#__PURE__*/ _jsx(PageBody, {
        children: /*#__PURE__*/ _jsx("div", {
            "data-testid": Testids.pageState.content,
            children: /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.evidenceBoard.page,
                className: "space-y-4",
                children: [
                    /*#__PURE__*/ _jsx(PageHero, {
                        title: "Proof & Evidence Board",
                        subtitle: "Every deliverable date in each program's current contract period, with its status and the attachments of its latest filing."
                    }),
                    /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                        getItemTestId: (m)=>m.id,
                        loading: fanoutLoading,
                        metrics: metrics
                    }),
                    /*#__PURE__*/ _jsx(SpecraFilterToolbar, {
                        filters: /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsxs(SpecraSelect, {
                                    "aria-label": "Filter by program",
                                    className: "w-auto",
                                    "data-testid": Testids.evidenceBoard.filterProgram,
                                    onChange: (e)=>setProgramFilter(e.target.value),
                                    size: "sm",
                                    value: programFilter,
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "all",
                                            children: "All programs"
                                        }),
                                        programs.map((p)=>/*#__PURE__*/ _jsx("option", {
                                                value: p.id,
                                                children: programName(p)
                                            }, p.id))
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(SpecraSelect, {
                                    "aria-label": "Filter by status",
                                    className: "w-auto",
                                    "data-testid": Testids.cmPortfolio.filterState,
                                    onChange: (e)=>setStateFilter(e.target.value),
                                    size: "sm",
                                    value: stateFilter,
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "all",
                                            children: "All statuses"
                                        }),
                                        OCCURRENCE_STATES.filter((s)=>s !== 'retired' && s !== 'waiting_on_event').map((s)=>/*#__PURE__*/ _jsx("option", {
                                                value: s,
                                                children: STATE_SHORT_LABEL[s]
                                            }, s))
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraSelect, {
                                    "aria-label": "Filter by filing",
                                    className: "w-auto",
                                    "data-testid": Testids.evidenceBoard.filterProof,
                                    onChange: (e)=>setProofFilter(e.target.value),
                                    size: "sm",
                                    value: proofFilter,
                                    children: Object.keys(PROOF_FILTER_LABEL).map((s)=>/*#__PURE__*/ _jsx("option", {
                                            value: s,
                                            children: PROOF_FILTER_LABEL[s]
                                        }, s))
                                })
                            ]
                        }),
                        search: false
                    }),
                    (programsTruncated || failedPrograms > 0) && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                        tone: failedPrograms > 0 ? 'danger' : 'warning',
                        variant: "framed",
                        children: [
                            programsTruncated && /*#__PURE__*/ _jsx("div", {
                                children: `More than ${PROGRAMS_CAP} programs are visible; the board and its totals cover only the first ${PROGRAMS_CAP}.`
                            }),
                            failedPrograms > 0 && /*#__PURE__*/ _jsxs("div", {
                                "data-testid": Testids.cmPortfolio.partialNote,
                                children: [
                                    "Deliverables could not be loaded for ",
                                    failedPrograms,
                                    ' ',
                                    failedPrograms === 1 ? 'program' : 'programs',
                                    "; the board and its totals leave",
                                    ' ',
                                    failedPrograms === 1 ? 'it' : 'them',
                                    " out."
                                ]
                            })
                        ]
                    }),
                    fanoutLoading ? /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        tone: "muted",
                        children: "Loading deliverables…"
                    }) : groups.length === 0 ? rows.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                        action: /*#__PURE__*/ _jsx(SpecraLink, {
                            href: "/delivery/programs",
                            renderLink: renderRouterLink,
                            children: "Open programs"
                        }),
                        "data-testid": Testids.evidenceBoard.emptyState,
                        description: "Dates appear once a program's deliverables have rules and a current contract period.",
                        framed: true,
                        title: "No deliverable dates fall in your programs’ current contract periods yet."
                    }) : /*#__PURE__*/ _jsx(SpecraEmptyState, {
                        action: /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: clearFilters,
                            size: "sm",
                            variant: "secondary",
                            children: "Clear filters"
                        }),
                        "data-testid": Testids.evidenceBoard.emptyState,
                        framed: true,
                        title: "No deliverable dates match the current filters."
                    }) : /*#__PURE__*/ _jsx(SpecraDataTable, {
                        columns: boardColumns,
                        expandedIds: expandedKey ? [
                            expandedKey
                        ] : [],
                        framed: true,
                        getExpandToggleLabel: (r, open)=>`${open ? 'Hide' : 'Show'} dates for ${r.group.duty?.title || 'Deliverable'}`,
                        getRowChildren: (r)=>r.occurrence ? undefined : r.group.rows.map((o)=>({
                                    group: r.group,
                                    occurrence: o
                                })),
                        getRowId: (r)=>r.occurrence ? `${r.group.key}/${r.occurrence.id}` : r.group.key,
                        getRowProps: (r)=>r.occurrence ? {
                                'data-instance-id': r.occurrence.id
                            } : {
                                className: 'cursor-pointer',
                                'data-group-key': r.group.dutyId,
                                onClick: (e)=>{
                                    if (e.target.closest('a, button')) return;
                                    toggleGroup(r.group.key);
                                }
                            },
                        onExpandedChange: (ids)=>{
                            const opened = ids.find((id)=>id !== expandedKey);
                            setExpandedKey(opened === undefined ? null : String(opened));
                        },
                        rows: groups.map((group)=>({
                                group
                            })),
                        rowTestId: (r)=>r.occurrence ? Testids.evidenceBoard.instanceRow : Testids.evidenceBoard.groupRow,
                        testId: Testids.evidenceBoard.table
                    })
                ]
            })
        })
    });
}
function dateHref(programId, occId) {
    return `/delivery/programs/${programId}/deliverables?occ=${occId}`;
}
// One deliverable: a scannable row (dates, next open due date, late / awaiting
// counts, filings with attachments), click-to-expand into its dates.
function groupFacts(group) {
    const n = group.rows.length;
    const filed = group.rows.filter((o)=>o.latestSubmission);
    return {
        n,
        late: group.rows.filter((o)=>o.status?.state === 'late').length,
        awaiting: group.rows.filter((o)=>o.status?.state === 'awaiting_review').length,
        approved: group.rows.filter((o)=>o.status?.state === 'approved').length,
        nextOpen: group.rows.find((o)=>isOpenState(o.status?.state ?? '') && o.dueOn),
        filed: filed.length,
        withProof: filed.filter((o)=>proofOf(o) === 'attached').length
    };
}
const boardColumns = [
    {
        id: 'program',
        header: 'Program',
        cell: ({ group, occurrence })=>occurrence ? null : /*#__PURE__*/ _jsx(SpecraLink, {
                href: `/delivery/programs/${group.programId}/deliverables`,
                renderLink: renderRouterLink,
                children: group.programName
            })
    },
    {
        id: 'deliverable',
        header: 'Deliverable',
        className: 'max-w-md',
        cell: ({ group, occurrence: o })=>{
            if (o) {
                return /*#__PURE__*/ _jsx(SpecraLink, {
                    href: dateHref(group.programId, o.id),
                    renderLink: renderRouterLink,
                    children: o.label || cmDate(o.dueOn, 'Date not stated')
                });
            }
            const { n } = groupFacts(group);
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(SpecraLink, {
                        href: `/delivery/programs/${group.programId}/deliverables/${group.dutyId}`,
                        renderLink: renderRouterLink,
                        children: [
                            group.duty?.letter && /*#__PURE__*/ _jsx("span", {
                                className: "mr-2 font-mono text-muted-foreground",
                                children: group.duty.letter
                            }),
                            group.duty?.title || 'Deliverable'
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "span",
                        className: "ml-2 whitespace-nowrap",
                        size: "meta",
                        tone: "muted",
                        children: [
                            "(",
                            n,
                            " ",
                            n === 1 ? 'date' : 'dates',
                            ")"
                        ]
                    })
                ]
            });
        }
    },
    {
        id: 'due',
        header: 'Due',
        className: 'whitespace-nowrap',
        cell: ({ group, occurrence: o })=>{
            if (o) return cmDate(o.dueOn, 'Not stated');
            const { nextOpen } = groupFacts(group);
            return nextOpen ? cmDate(nextOpen.dueOn) : /*#__PURE__*/ _jsx("span", {
                className: "text-muted-foreground",
                children: "Nothing open"
            });
        }
    },
    {
        id: 'status',
        header: 'Status',
        className: 'whitespace-nowrap',
        cell: ({ group, occurrence: o })=>{
            if (o) {
                return /*#__PURE__*/ _jsx(CmStatusBadge, {
                    status: occurrenceStatus(o, group.duty?.finalReviewTier, todayISO())
                });
            }
            const { n, approved, late, awaiting } = groupFacts(group);
            return /*#__PURE__*/ _jsxs("span", {
                className: "inline-flex items-center gap-1.5",
                children: [
                    /*#__PURE__*/ _jsxs(SpecraBadge, {
                        variant: approved === n ? 'success' : 'neutral',
                        children: [
                            approved,
                            "/",
                            n,
                            " approved"
                        ]
                    }),
                    late > 0 && /*#__PURE__*/ _jsxs(SpecraBadge, {
                        variant: "danger",
                        children: [
                            late,
                            " late"
                        ]
                    }),
                    awaiting > 0 && /*#__PURE__*/ _jsxs(SpecraBadge, {
                        variant: "info",
                        children: [
                            awaiting,
                            " awaiting review"
                        ]
                    })
                ]
            });
        }
    },
    {
        id: 'filing',
        header: 'Latest filing',
        className: 'whitespace-nowrap',
        cell: ({ group, occurrence: o })=>{
            if (!o) {
                const { n, filed } = groupFacts(group);
                return /*#__PURE__*/ _jsxs("span", {
                    className: "text-muted-foreground",
                    children: [
                        filed,
                        "/",
                        n,
                        " filed"
                    ]
                });
            }
            const sub = o.latestSubmission;
            return sub ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "Filing ",
                    sub.attempt,
                    ", ",
                    cmDate(sub.submittedOn, 'date not stated')
                ]
            }) : /*#__PURE__*/ _jsx("span", {
                className: "text-muted-foreground",
                children: "Not filed"
            });
        }
    },
    {
        id: 'attachments',
        header: 'Attachments',
        cell: ({ group, occurrence: o })=>{
            if (!o) {
                const { filed, withProof } = groupFacts(group);
                return /*#__PURE__*/ _jsx("span", {
                    className: "whitespace-nowrap text-muted-foreground",
                    children: filed === 0 ? 'Nothing filed' : `${withProof}/${filed} with attachments`
                });
            }
            const sub = o.latestSubmission;
            if (!sub) return null;
            if (sub.attachments.length === 0) {
                return /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: "No attachments"
                });
            }
            return /*#__PURE__*/ _jsx("span", {
                className: "flex flex-wrap gap-x-3 gap-y-1",
                children: sub.attachments.map((a, i)=>a.url ? /*#__PURE__*/ _jsx(SpecraLink, {
                        href: a.url,
                        target: "_blank",
                        rel: "noreferrer",
                        "data-testid": Testids.cmPortfolio.attachment,
                        children: a.label || 'Attachment'
                    }, a.id || i) : /*#__PURE__*/ _jsx(SpecraLink, {
                        href: dateHref(group.programId, o.id),
                        renderLink: renderRouterLink,
                        "data-testid": Testids.cmPortfolio.attachment,
                        children: a.label || 'Attachment'
                    }, a.id || i))
            });
        }
    }
];
